import { useCallback, useState } from "react";

import { api, ApiError } from "./client";
import { useResource } from "./useResource";

export type ApiConnection = {
  readonly display_name: string;
  readonly last_error: string | null;
  readonly last_synced_at: string | null;
  readonly provider: string;
  readonly public_id: string;
  readonly status: string;
  readonly workspace_public_id: string;
};

export type UseConnections = {
  readonly connections: ReadonlyArray<ApiConnection>;
  readonly error: ApiError | undefined;
  readonly loading: boolean;
  readonly refetch: () => void;
};

export const useConnections = (): UseConnections => {
  const { data, error, loading, refetch } =
    useResource<ReadonlyArray<ApiConnection>>("/connections");
  return { connections: data ?? [], error, loading, refetch };
};

export const disconnectConnection = (id: string): Promise<unknown> =>
  api.del(`/connections/${id}`);

export type StartConnectionResult = {
  readonly intent_public_id: string;
  readonly provider: string;
  // The provider authorize / App-install URL to send the browser to, or null
  // when the provider isn't configured on this server (credential-less dev).
  readonly redirect_to: string | null;
};

// POST /connections/start — begins a provider connection (onboarding.md
// §Connecting GitHub). Returns the URL to redirect the browser to.
//
// `targetLogin` names the GitHub account to install on. Every connect after the
// first has to supply it: an install URL that doesn't name its target is
// resolved by github.com to an installation the viewer already has, so the
// browser never reaches the new organisation.
export const startConnection = (
  provider: "github",
  workspaceId?: string,
): Promise<StartConnectionResult> =>
  api.post("/connections/start", {
    provider,
    ...(workspaceId ? { workspace_id: workspaceId } : {}),
  });

// GET /connections/:id/available-repositories — the direct-link list in
// Settings → Connections (onboarding.md §Listing and linking repositories).
// The server serves it from its cache of the connection's repositories — and
// fills that cache from the provider when it is empty — so the dashboard can
// simply ask for the list and get one.
export type ApiAvailableRepo = {
  readonly already_linked: boolean;
  readonly branches: ReadonlyArray<string>;
  readonly default_branch: string;
  readonly has_config: boolean;
  readonly private: boolean;
  readonly provider_full_name: string;
  readonly provider_repo_id: string;
};

export type ApiAvailableRepositories = {
  readonly next_cursor: string | null;
  readonly repositories: ReadonlyArray<ApiAvailableRepo>;
  readonly synced_at: string | null;
};

// POST /connections/:id/available-repositories/refresh — re-reads the whole
// list from the provider and replaces the cache. The slow path, so it's an
// explicit act (the connection's refresh button) rather than something every
// read pays for.
export const refreshAvailableRepositories = (
  connectionId: string,
): Promise<ApiAvailableRepositories> =>
  api.post(`/connections/${connectionId}/available-repositories/refresh`, {});

export type UseAvailableRepositories = {
  readonly error: ApiError | undefined;
  readonly loading: boolean;
  readonly refetch: () => void;
  // Pull a new list from the provider, then re-read it.
  readonly refresh: () => void;
  readonly refreshing: boolean;
  readonly repositories: ReadonlyArray<ApiAvailableRepo>;
};

export const useAvailableRepositories = (
  connectionId: string | null,
): UseAvailableRepositories => {
  const { data, error, loading, refetch } =
    useResource<ApiAvailableRepositories>(
      connectionId
        ? `/connections/${connectionId}/available-repositories`
        : null,
    );
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<ApiError | undefined>(
    undefined,
  );

  const refresh = useCallback((): void => {
    if (!connectionId) {
      return;
    }
    setRefreshing(true);
    setRefreshError(undefined);
    // Re-read rather than adopting the POST's own body: the GET stays the one
    // source of truth for the list, and it now costs a cached read.
    void refreshAvailableRepositories(connectionId)
      .then(() => refetch())
      .catch((err: unknown) =>
        setRefreshError(
          err instanceof ApiError
            ? err
            : new ApiError("unknown", "Couldn't refresh repositories.", 0),
        ),
      )
      .finally(() => setRefreshing(false));
  }, [connectionId, refetch]);

  return {
    error: error ?? refreshError,
    loading,
    refetch,
    refresh,
    refreshing,
    repositories: data?.repositories ?? [],
  };
};

export type LinkRepositoryInput = {
  readonly base_branch?: string;
  readonly provider_full_name: string;
  readonly provider_repo_id: string;
  readonly workspace_id: string;
};

export type LinkIntentRef = {
  readonly provider_full_name: string;
  readonly public_id: string;
  readonly status: string;
};

// POST /connections/:id/link — links one repository, returns the link intent to
// poll (onboarding.md §Link).
export const linkRepository = (
  connectionId: string,
  input: LinkRepositoryInput,
): Promise<{ link_intent: LinkIntentRef }> =>
  api.post(`/connections/${connectionId}/link`, input);

// GET /connections/:id/link-intents/:link_id — the link intent's live status.
// `repository` is filled in once the intent worker has inserted the repositories
// row; the link flow polls this and navigates to the new repo when it appears.
export type LinkIntentStatus = {
  readonly last_error: string | null;
  readonly provider_full_name: string;
  readonly repository: { readonly public_id: string } | null;
  readonly status: string;
};

export const getLinkIntent = (
  connectionId: string,
  linkId: string,
): Promise<LinkIntentStatus> =>
  api.get(`/connections/${connectionId}/link-intents/${linkId}`);

// The repository scan (inspect_repository) still exists on the server; the
// dashboard no longer drives it since the connect wizard was removed. Repos are
// linked from Settings → Connections (see linkRepository above).
